import { Footer } from '@/components/common/Footer';
import { useEtatDuClient } from '@/hooks/useEstClientSeul';
import { OpenPayeCredentialsAlertModal } from '@/components/common/OpenPayeCredentialsAlertModal';
import { SenderReconnectAlertModal } from '@/components/common/SenderReconnectAlertModal';
import { VersionBubble } from '@/components/common/VersionBubble';
import { AnnonceAssistant } from '@/features/assistant/components/AnnonceAssistant';
import { useAnnonceAssistant } from '@/features/assistant/hooks/useAnnonceAssistant';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ROUTES } from '@/constants/app';
import { useLogout } from '@/hooks/useAuth';
import { useConfirmSepaSetup, useSepaStatus } from '@/hooks/useBilling';
import { toast } from 'sonner';
import { SepaRequiredBanner } from '@/components/Billing/SepaRequiredBanner';
import { useOrganisations } from '@/hooks/useOrganisations';
import { useAuthStore } from '@/store/auth.store';
import { useOrganisationsStore } from '@/store/organisations.store';
import {
  Archive,
  AlertCircle,
  CalendarDays,
  CheckCheck,
  CheckCircle,
  FileText,
  FolderTree,
  Folders,
  GitGraph,
  Home,
  Loader2 as Loader2Icon,
  Mail,
  Receipt,
  Briefcase,
  BookOpen,
} from 'lucide-react';
import { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { useMarque } from '@/hooks/useMarque';
import {
  destinationClientParDefaut,
  destinationEstAutoriseePourClient,
  destinationsNavigationClient,
} from './navigation-client';

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const marqueAppliquee = useMarque();
  const utilisateur = useAuthStore(s => s.user);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSepaSuccessModal, setShowSepaSuccessModal] = useState(false);
  const isNotPaymentPage = true;

  // Load organisations at layout level to ensure org is available before any child renders
  const { isLoading: isOrgsLoading } = useOrganisations();
  const currentOrganisation = useOrganisationsStore(s => s.currentOrganisation);
  const organisations = useOrganisationsStore(s => s.organisations);

  // Prefetch SEPA status at layout level to avoid multiple API calls from child components
  // React Query will cache this for 5 minutes, so child components can use it without additional requests
  const { data: sepa } = useSepaStatus();
  const confirmerMoyenDePaiement = useConfirmSepaSetup();

  // [2026-09] Décision Rose du 19/09/2026 : à la connexion, on PROPOSE le mandat
  // de prélèvement à qui n'en a pas — on ne bloque pas. Le garde `SepaGuard`
  // ne joue qu'au clic sur une automatisation ; or un client dont les
  // automatisations tournent en planifié peut ne jamais cliquer, et ses
  // factures partent alors sans pouvoir être prélevées (cas réel : facture du
  // 16/09 émise, prélèvement en échec, aucun mandat enregistré).
  // La bannière ne s'affiche que si le statut est CONNU et négatif : tant que
  // la réponse n'est pas là, ou en cas d'erreur, on ne dit rien plutôt que de
  // réclamer un mandat à quelqu'un qui en a un. Un administrateur de la
  // plateforme n'est pas un client facturé : on ne lui demande rien.
  // Et seulement à qui peut agir : un simple membre du cabinet n'a pas le
  // droit de poser la carte (verdict QA du 20/09/2026), on ne le sollicite pas.
  const proposerLeMandat =
    utilisateur?.role !== 'admin' &&
    sepa !== undefined &&
    sepa.hasValidMandate === false &&
    sepa.isInGracePeriod === false &&
    sepa.canManage !== false;

  // L'annonce de l'assistant à la première connexion (décision Rose
  // 12/09/2026) : tant que l'information n'est pas validée, elle REMPLACE
  // l'application — un écran, une tâche. Seule sa documentation reste
  // joignable, pour pouvoir la lire avant de valider. L'application se rend
  // sans attendre la réponse : l'écran ne s'impose qu'à « à valider ».
  // (Le profil est lu plus bas — `useEtatDuClient` — et décide AVANT
  // l'annonce : une entreprise cliente n'a pas l'assistant, on ne lui
  // demande rien, et le serveur n'est pas interrogé tant qu'on ne sait pas.)

  // Cabinet ou entreprise cliente : la réponse change le menu ET la page d'arrivée.
  const {
    estClientSeul,
    bdeseOuverte,
    folderUuid,
    aDesEntreprises,
    erreurChargement,
    peutVoirAbsences,
    peutVoirApprobations,
  } = useEtatDuClient();
  const annonce = useAnnonceAssistant({ actif: estClientSeul === false });
  const logout = useLogout();
  const navigate = useNavigate();

  // Navigation items - Le menu Coffre-Fort est masqué pour les gestionnaires
  // Seuls les vault_user (employés) ont accès via leur interface dédiée (/my-vaults)
  const navigation = useMemo(() => {
    const baseNavigation = [
      {
        name: t('navigation.dashboard', 'Dashboard'),
        href: ROUTES.DASHBOARD,
        icon: Home,
      },
      {
        name: t('navigation.domains', 'Domaines'),
        href: ROUTES.DOMAINS,
        icon: FolderTree,
      },
      {
        name: t('navigation.folders', 'Tous les dossiers'),
        href: ROUTES.FOLDERS,
        icon: Folders,
      },
      {
        name: t('navigation.automation', 'Automatisations'),
        href: ROUTES.AUTOMATION,
        icon: GitGraph,
      },
      {
        name: t('navigation.documents', 'Documents'),
        href: ROUTES.DOCUMENTS,
        icon: Archive,
      },
      // C3 — SIRH. Une notion ajoutée doit être atteignable depuis le menu :
      // un écran joignable seulement par URL est un écran que personne n'utilise.
      {
        name: t('navigation.leave', 'Absences'),
        href: ROUTES.SIRH.MY_LEAVE,
        icon: CalendarDays,
      },
      {
        name: t('navigation.leaveApprovals', 'Approbations'),
        href: ROUTES.SIRH.APPROVALS,
        icon: CheckCheck,
      },
      // Menu Coffre-Fort retiré pour les gestionnaires
      // Les gestionnaires n'ont plus accès direct au contenu des coffres-forts
      // Ils peuvent voir l'historique via le drawer dans la page du dossier
      {
        name: t('navigation.templates', 'Templates'),
        href: ROUTES.TEMPLATES,
        icon: FileText,
      },
      {
        name: t('navigation.senders', 'Expéditeurs'),
        href: '/senders',
        icon: Mail,
      },
      {
        name: t('navigation.billing', 'Facturation'),
        href: '/settings/billing',
        icon: Receipt,
      },
    ];

    // [25/08/2026] Une entreprise cliente ne voit PAS le menu du cabinet.
    //
    // Sans ce filtre, la direction d'un client arrivait dans « Tous les
    // dossiers » avec Domaines, GED, Templates, Expéditeurs et Facturation sous
    // les yeux. Aucune donnée ne fuyait — le cloisonnement de C1 tient — mais
    // elle voyait l'intérieur du logiciel de son cabinet de paie.
    //
    // On RETIRE plutôt que de reconstruire une seconde liste : une deuxième
    // liste divergerait au premier ajout de menu, et personne ne s'en
    // apercevrait avant qu'un client ne voie de travers.
    if (estClientSeul) {
      const destinations = new Set(
        destinationsNavigationClient({
          aDesEntreprises,
          peutVoirAbsences,
          peutVoirApprobations,
          bdeseOuverte,
          folderUuid,
        })
      );
      // Son espace EN PREMIER, et pas relégué en bas de barre : c'est son point
      // d'entrée, l'équivalent de « Tous les dossiers » pour un cabinet.
      // Le tableau de bord n'y figure pas — c'est celui du CABINET.
      return [
        ...(destinations.has('/mon-entreprise')
          ? [
              {
                name: t('navigation.monEntreprise', 'Mon entreprise'),
                href: '/mon-entreprise',
                icon: Briefcase,
              },
            ]
          : []),
        // Et ce que son cabinet lui a OUVERT, rien de plus. Proposer un écran
        // sur un module fermé se solderait par un refus du serveur : autant ne
        // pas le proposer. C'est tout l'intérêt des interrupteurs — ils
        // décident aussi de ce que le client VOIT.
        ...baseNavigation.filter(entree => destinations.has(entree.href)),
        // La base économique et sociale, si son cabinet la lui a ouverte.
        // Elle est adressée par DOSSIER — une entreprise ne connaît pas ses
        // dossiers, c'est le serveur qui lui dit lequel la concerne.
        ...(bdeseOuverte &&
        folderUuid &&
        destinations.has(`/bdese/${folderUuid}`)
          ? [
              {
                name: t('navigation.bdese', 'Base économique et sociale'),
                href: `/bdese/${folderUuid}`,
                icon: BookOpen,
              },
            ]
          : []),
        ...baseNavigation.filter(entree => entree.href === '/notifications'),
      ];
    }

    return baseNavigation;
  }, [
    t,
    estClientSeul,
    aDesEntreprises,
    peutVoirAbsences,
    peutVoirApprobations,
    bdeseOuverte,
    folderUuid,
  ]);

  // Fermer la sidebar sur changement de route (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Note: The blocking modal will prevent navigation, and users must click
  // the button to go to senders page. No automatic redirect needed here.

  // Retour de Stripe après l'enregistrement d'une carte (cas 3-D Secure : la
  // page a été quittée puis rechargée). Stripe ajoute `setup_intent=seti_…` à
  // l'URL de retour : on rattache tout de suite le moyen de paiement au compte
  // — le webhook reste le filet — puis on confirme à l'écran.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sepaSetupStatus = urlParams.get('sepa_setup');
    const setupIntentId = urlParams.get('setup_intent');
    const redirectStatus = urlParams.get('redirect_status');

    if (sepaSetupStatus === 'success') {
      const authentificationEchouee = () =>
        toast.error(
          t(
            'billing.sepa.authFailed',
            "L'authentification de votre carte a échoué. Réessayez, ou utilisez une autre carte."
          )
        );
      // « Carte enregistrée » ne s'affiche qu'une fois le rattachement
      // confirmé par l'API ; un lien forgé, une authentification bancaire
      // refusée ou un rattachement en échec ne montrent jamais un succès
      // (verdicts sécurité et QA du 20/09/2026).
      if (redirectStatus === 'failed') {
        authentificationEchouee();
      } else if (setupIntentId) {
        confirmerMoyenDePaiement.mutate(setupIntentId, {
          onSuccess: () => setShowSepaSuccessModal(true),
          onError: (erreur: unknown) => {
            // 400 = Stripe n'a pas confirmé le SetupIntent (authentification
            // refusée) ; le reste = enregistré chez Stripe mais pas chez nous.
            const statut = (erreur as { response?: { status?: number } })
              ?.response?.status;
            if (statut === 400) {
              authentificationEchouee();
            } else {
              toast.error(
                t(
                  'billing.sepa.confirmFailed',
                  'La carte est enregistrée chez Stripe mais son rattachement à votre compte a échoué. Rechargez la page ; si le problème persiste, écrivez-nous.'
                )
              );
            }
          },
        });
      }

      // Clean up URL by removing Stripe's parameters
      urlParams.delete('sepa_setup');
      urlParams.delete('setup_intent');
      urlParams.delete('setup_intent_client_secret');
      urlParams.delete('redirect_status');
      const newUrl = `${window.location.pathname}${
        urlParams.toString() ? `?${urlParams.toString()}` : ''
      }${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Block rendering until organisation is loaded (prevents API calls without organisationUuid)
  if (
    isOrgsLoading ||
    (organisations.length > 0 && !currentOrganisation) ||
    estClientSeul === null
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        {erreurChargement ? (
          <div className="mx-4 max-w-md rounded-xl border border-neutral-200 bg-white p-6 text-center shadow-sm">
            <AlertCircle className="text-warning-600 mx-auto h-8 w-8" />
            <h1 className="mt-3 text-base font-semibold text-neutral-900">
              Accès momentanément indisponible
            </h1>
            <p className="mt-2 text-sm leading-6 text-neutral-600">
              Nous n'avons pas pu vérifier votre espace. Réessayez avant de
              poursuivre.
            </p>
            <Button className="mt-4" onClick={() => window.location.reload()}>
              Réessayer
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Loader2Icon className="text-primary-600 h-8 w-8 animate-spin" />
            <p className="text-sm text-neutral-500">
              {t('common.loading', 'Chargement...')}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (
    estClientSeul === false &&
    annonce.aValider &&
    location.pathname !== ROUTES.AIDE.ASSISTANT
  ) {
    return (
      <AnnonceAssistant
        onValider={annonce.valider}
        enCours={annonce.validationEnCours}
        enErreur={annonce.validationEnErreur}
        onSeDeconnecter={async () => {
          try {
            await logout.mutateAsync();
          } catch {
            // L'erreur est déjà gérée dans useLogout ; on quitte quand même.
          }
          navigate(ROUTES.AUTH.LOGIN);
        }}
      />
    );
  }

  const capacitesClient = {
    aDesEntreprises,
    peutVoirAbsences,
    peutVoirApprobations,
    bdeseOuverte,
    folderUuid,
  };

  // Le menu personnalisé ne doit pas être contournable avec une ancienne URL.
  // Le contrôle a lieu avant `children` : l'écran inutile ne se monte pas et
  // ne déclenche donc aucun appel API qui finirait en 403 dans la console.
  if (
    estClientSeul &&
    !destinationEstAutoriseePourClient(location.pathname, capacitesClient)
  ) {
    return (
      <Navigate to={destinationClientParDefaut(capacitesClient)} replace />
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Sidebar mobile avec overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-gray-900/80 transition-opacity"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-full max-w-xs">
            <Sidebar
              navigation={navigation}
              currentPath={location.pathname}
              onClose={() => setSidebarOpen(false)}
              isMobile={true}
              estClientSeul={estClientSeul}
              aDesEntreprises={aDesEntreprises}
            />
          </div>
        </div>
      )}

      {/* Sidebar desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <Sidebar
          navigation={navigation}
          currentPath={location.pathname}
          isMobile={false}
          estClientSeul={estClientSeul}
          aDesEntreprises={aDesEntreprises}
        />
      </div>

      {/* Contenu principal avec marge pour la sidebar desktop */}
      <div className="flex flex-1 flex-col lg:pl-72">
        <Header onMenuClick={() => setSidebarOpen(true)} />

        <main className="flex-1 py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            {/* SubscriptionStatus retiré avec l'ancien système d'abonnement */}
            {proposerLeMandat && (
              <div className="mb-6">
                <SepaRequiredBanner />
              </div>
            )}
            {isNotPaymentPage && <div className="h-4" />}
            {children}
          </div>
        </main>

        <Footer variant="app" />
      </div>

      {/* Les nouveautés sont une information à aller chercher, pas une tâche
          imposée au milieu du parcours. Elles restent accessibles au cabinet
          via la bulle de version et la page changelog. Un salarié, un manager,
          un employeur ou un élu CSE n'a pas besoin de connaître les évolutions
          DSN/GED de l'outil de son cabinet : on ne lui affiche donc ni la bulle
          ni l'ancienne modale bloquante. */}
      {!estClientSeul && marqueAppliquee.parDefaut && <VersionBubble />}
      <OpenPayeCredentialsAlertModal />
      <SenderReconnectAlertModal />

      {/* Carte enregistrée (retour de Stripe) */}
      <Modal
        isOpen={showSepaSuccessModal}
        onClose={() => setShowSepaSuccessModal(false)}
        title={t('billing.sepa.success.title', 'Carte enregistrée')}
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-center">
            <div className="bg-success-100 flex h-16 w-16 items-center justify-center rounded-full">
              <CheckCircle className="text-success-600 h-10 w-10" />
            </div>
          </div>

          <div className="space-y-2 text-center">
            <p className="text-sm leading-relaxed text-neutral-700">
              {t(
                'billing.sepa.success.description',
                "Votre carte est enregistrée. Vos factures seront réglées automatiquement ; vous pouvez utiliser toutes les fonctionnalités d'automatisation."
              )}
            </p>

            <div className="bg-primary-50 border-primary-200 rounded-xl border p-4 text-left">
              <p className="text-primary-900 mb-1 text-xs font-medium">
                {t('billing.sepa.success.reminder', 'Rappel de facturation :')}
              </p>
              <p className="text-primary-800 text-xs">
                {t(
                  'billing.sepa.success.pricing',
                  '1€ HT par dossier utilisé dans vos automatisations chaque mois.'
                )}
              </p>
            </div>
          </div>

          <div className="flex justify-center pt-2">
            <Button
              onClick={() => setShowSepaSuccessModal(false)}
              className="px-8"
            >
              {t('common.buttons.understood', 'Compris')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
